import { api } from './client';
import type { PAList } from './money';

/**
 * ============================================================================
 * THE NEWS CLIENT — §28 «Сообщения» and §18's category checklist (F28)
 * ============================================================================
 *
 *   GET  /pa/news?category_id=&unread=&offset=       — what is addressed to ME
 *   GET  /pa/news/categories                         — the checklist, with HIS numbers
 *   POST /pa/news/categories/{id}/subscription       — ONE boolean on ONE category
 *   GET  /pa/news/{id}                               — one announcement
 *   POST /pa/news/{id}/read                          — record that he opened it
 *
 * It mirrors the wire structs in `backend/internal/personalarea/news.go`, pinned BOTH WAYS
 * to `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ============================================================================
 * ⚠ FOUR RULES
 * ============================================================================
 *
 * 1. **`body` IS FREE TEXT AND IS RENDERED AS TEXT.** The authoring screen accepts free
 *    text, `news_item.body` has no format column, and a portal interpreting it as HTML would
 *    make every operator announcement an injection surface on the subscriber's own session.
 *    This project has already ruled that way once, for the payment-instruction blocks.
 *    `NewsScreen.test.tsx` renders a body containing a `<script>` and asserts it appears as
 *    characters, and sweeps the sources for `dangerouslySetInnerHTML`.
 *
 * 2. **MARK-AS-READ IS AN EXPLICIT ACT, NEVER A SIDE EFFECT OF A READ.** S22 defines «read»
 *    as «the item was opened or its pop-up dismissed» — a gesture the screen knows about and
 *    the transport does not. A counter cleared by anything that polls is a counter that is
 *    always zero, and the operator's own «кому показали объявление?» report is built from
 *    these rows: a mark-read the subscriber never made would make that answer name people
 *    who were never shown it, which is worse than a missing row because it looks like
 *    evidence.
 *
 * 3. **`subscribed` IS DERIVED ON THE SERVER AND NEVER HERE.** The model is OPT-OUT
 *    («absence means shown»), the corpus is not unanimous about it (Appendix L §2.17 records
 *    the open question), and a client copy of a rule whose two readings the spec itself
 *    disagrees about is a copy that will be wrong for one of them. `chosen` — whether a row
 *    exists at all — is the fact that distinguishes «он решил» from «его не спрашивали».
 *
 * 4. **ONE BOOLEAN PER WRITE, NEVER «here are my categories».** A full-replacement body
 *    would zero every category the screen did not render — the same D109 reasoning that
 *    makes the notification write sparse. A MANDATORY category is refused in EITHER
 *    direction (`409 pa.news_category_mandatory`), so the screen offers no control for one.
 */

/**
 * NEWS_PAGE_SIZE mirrors `newsPageMax` — the server's page, not the caller's. Pinned to the
 * Go constant by `src/api/page-size.test.ts`, because every fixture here is smaller than one
 * page and a drifted mirror would skip or repeat rows only on a real estate.
 */
export const NEWS_PAGE_SIZE = 50;

/** `PANewsCategory` — one row of §18's «Категории сообщений», with HIS numbers. */
export interface PANewsCategory {
  id: number;
  name: string;
  /**
   * §2.8's «Нельзя отписаться» — outages and maintenance. The checkbox is rendered FIXED
   * rather than absent: he has to be able to SEE that these announcements reach him and
   * that he cannot switch them off.
   */
  mandatory: boolean;
  /** DERIVED on the server under the opt-out model — see rule 3. */
  subscribed: boolean;
  /** Whether a `news_subscription` row exists at all: «он решил» vs «его не спрашивали». */
  chosen: boolean;
  /** HIS live item count in this category, not the operator's `item_count`. */
  live: number;
  unread: number;
}

/** `PANewsCategories` — the checklist and the section's own unread badge. */
export interface PANewsCategories {
  items: PANewsCategory[];
  total: number;
  /** Summed on the SERVER, so the screen never adds up a page it may have truncated. */
  unread_total: number;
}

/** `PANewsSubscriptionRequest` — one boolean on one category. See rule 4. */
export interface PANewsSubscriptionRequest {
  subscribed: boolean;
}

/** `PANewsItem` — one announcement (§28: date, title, body). */
export interface PANewsItem {
  id: number;
  category_id: number;
  category_name: string;
  mandatory: boolean;
  subject: string;
  /** ⚠ THE OPERATOR'S OWN TEXT. Rendered as TEXT — see rule 1. */
  body: string;
  published_at: string;
  read: boolean;
}

// ---- calls ------------------------------------------------------------------

/**
 * listNews reads one page of what is addressed to HIM.
 *
 * The three filters are the ones the path declares and no others: an unknown query key is
 * IGNORED by the server, so a screen sending one answers about an UNFILTERED set and looks
 * like a filter that found everything. `wire-spec.test.ts` pins the key set.
 */
export function listNews(
  opts: { categoryID?: number | null; unread?: boolean; offset?: number } = {},
): Promise<PAList<PANewsItem>> {
  const q = new URLSearchParams();
  if (opts.categoryID) q.set('category_id', String(opts.categoryID));
  if (opts.unread) q.set('unread', 'true');
  if (opts.offset) q.set('offset', String(opts.offset));
  const query = q.toString();
  return api.get<PAList<PANewsItem>>(`/pa/news${query ? `?${query}` : ''}`);
}

export function getNewsCategories(): Promise<PANewsCategories> {
  return api.get<PANewsCategories>('/pa/news/categories');
}

/**
 * setNewsSubscription writes ONE boolean on ONE category and returns THAT category.
 *
 * The response is the state the screen renders — `subscribed` is derived on the server and
 * a re-tick of the same value keeps the row it already had, which no client rule can know.
 */
export function setNewsSubscription(id: number, subscribed: boolean): Promise<PANewsCategory> {
  const body: PANewsSubscriptionRequest = { subscribed };
  return api.post<PANewsCategory>(`/pa/news/categories/${id}/subscription`, body);
}

export function getNewsItem(id: number): Promise<PANewsItem> {
  return api.get<PANewsItem>(`/pa/news/${id}`);
}

/**
 * markNewsRead records the gesture and returns the fresh item.
 *
 * Idempotent on the server, which keeps the FIRST timestamp — the one the operator's readers
 * report should show.
 */
export function markNewsRead(id: number): Promise<PANewsItem> {
  return api.post<PANewsItem>(`/pa/news/${id}/read`);
}
